import { useMemo } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CHART_ANIMATION_DURATION_MS,
  CHART_ANIMATION_EASING,
  chartMountKey,
  seriesAnimationBegin,
} from "./chartAnimation";
import { NbsChartLegend } from "./NbsChartLegend";

export type NbsLineChartProps = {
  readonly data: readonly Record<string, string | number>[];
  readonly xKey: string;
  readonly seriesKeys: readonly string[];
  readonly height?: number;
  readonly colors?: readonly string[];
};

const DEFAULT_COLORS = ["#1f6f4a", "#c47b2c", "#2f5d8c", "#8a3b3b"];

export function NbsLineChart({
  data,
  xKey,
  seriesKeys,
  height = 280,
  colors = DEFAULT_COLORS,
}: NbsLineChartProps) {
  const chartData = useMemo(() => [...data], [data]);
  const mountKey = chartMountKey(chartData, xKey, seriesKeys);

  return (
    <div data-testid="nbs-line-chart" style={{ width: "100%" }}>
      <div style={{ width: "100%", height }}>
        <ResponsiveContainer width="100%" height="100%" debounce={50}>
          <LineChart
            key={mountKey}
            data={chartData}
            margin={{ top: 12, right: 16, left: 4, bottom: 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" vertical={false} />
            <XAxis
              dataKey={xKey}
              tick={{ fontSize: 12, fill: "#5c6b63" }}
              tickLine={false}
              axisLine={{ stroke: "rgba(0,0,0,0.12)" }}
            />
            <YAxis tick={{ fontSize: 12, fill: "#5c6b63" }} tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={{
                borderRadius: 10,
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
              }}
            />
            {seriesKeys.map((key, i) => {
              const color = colors[i % colors.length]!;
              return (
                <Line
                  key={`${mountKey}-${key}`}
                  type="monotone"
                  dataKey={key}
                  name={key}
                  stroke={color}
                  strokeWidth={2.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  isAnimationActive
                  animationBegin={seriesAnimationBegin(i)}
                  animationDuration={CHART_ANIMATION_DURATION_MS}
                  animationEasing={CHART_ANIMATION_EASING}
                  dot={{ r: 3.5, strokeWidth: 1.5, stroke: "#fff", fill: color }}
                  activeDot={{ r: 6, strokeWidth: 2, stroke: "#fff" }}
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <NbsChartLegend seriesKeys={seriesKeys} colors={colors} />
    </div>
  );
}
